import Papa from 'papaparse';
import PropTypes from 'prop-types';
import React, { useState } from 'react';

import Button from 'common-src/components/base/Button/Button';
import { apiRequest } from 'common-src/features/rest';

import useAlert from 'src/hooks/useAlert';
import BasePopup from 'src/popups/BasePopup';
import { parseCsvRows } from 'src/utils/documentationCsvUtils';

import styles from './SendBulkDocumentationPopup.module.scss';

const SendBulkDocumentationPopup = ({ onClose }) => {
  const [csvFile, setCsvFile] = useState(null);
  const [parsingError, setParsingError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { showAlert, AlertType } = useAlert();

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const isCsv = file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv');
    if (!isCsv) {
      setParsingError('Only CSV files are allowed.');
      setCsvFile(null);
      return;
    }
    setCsvFile(file);
    setParsingError('');
  };

  const handleGenerate = async () => {
    if (!csvFile) return;
    setIsLoading(true);
    setParsingError('');
    Papa.parse(csvFile, {
      header: true,
      skipEmptyLines: true,
      complete: async (res) => {
        try {
          const yesNoToBool = (val) => String(val).toLowerCase() === 'yes';
          const parsedRows = parseCsvRows(res.data, yesNoToBool);
          const resp = await apiRequest({
            endpoint: 'jobRequests/bulkDocumentation',
            method: 'POST',
            body: { items: parsedRows },
          });
          const json = await resp.json();
          if (resp.ok && json.msg === 'ok') {
            showAlert(
              AlertType.Success,
              '',
              "Bulk documentation generation started! You'll receive an email with the download link when ready.",
            );
            onClose?.(true);
          } else {
            let errorMsg = 'Failed to generate documentation.';
            if (json.errors && json.errors.length > 0) {
              errorMsg = json.errors.join(', ');
            } else if (json.message) {
              errorMsg = json.message;
            }
            showAlert(AlertType.Error, '', errorMsg);
            onClose?.(true);
          }
        } catch (err) {
          console.error('Outer error:', err);
          showAlert(AlertType.Error, '', 'Failed to process CSV or generate documentation.');
        } finally {
          setIsLoading(false);
        }
      },
      error: () => setParsingError('Failed to parse CSV file.'),
    });
  };

  return (
    <BasePopup
      id="generate-bulk-documentation"
      open
      onClose={isLoading ? undefined : onClose}
      title="Generate Bulk Documentation"
    >
      <div className={['p-l-30', 'p-r-30', 'p-t-20', 'p-b-5'].join(' ')}>
        <div className={styles.label}>Upload your CSV file</div>
        <input
          type="file"
          accept=".csv"
          onChange={handleFileChange}
          style={{ marginBottom: 16 }}
          disabled={isLoading}
        />
        {parsingError && <div className={styles.error}>{parsingError}</div>}
        <div className={styles.buttonsContainer}>
          <Button
            id="submit-button"
            text={isLoading ? 'Generating...' : 'Generate Documentation'}
            onClick={handleGenerate}
            disabled={!csvFile || isLoading}
            isLoading={isLoading}
            width={200}
          />
        </div>
        <div style={{ marginTop: 8, color: '#888', fontSize: 13, textAlign: 'right' }}>
          {isLoading && 'Generation in progress. Please do not close this window...'}
        </div>
      </div>
    </BasePopup>
  );
};

SendBulkDocumentationPopup.propTypes = {
  onClose: PropTypes.func,
};

export default SendBulkDocumentationPopup;
